import { Component, computed, inject, signal } from '@angular/core';
import { CdkDrag, CdkDragEnd, CdkDragMove } from '@angular/cdk/drag-drop';
import {
  FaIconComponent,
  FaIconLibrary,
} from '@fortawesome/angular-fontawesome';
import { FlowpilotSidebarService } from '@core/components/sidebar/components/flowpilot/window/content/services/flowpilot-sidebar.service';
import { faAlignLeft } from '@fortawesome/free-solid-svg-icons';
import { FlowpilotChatsContainerComponent } from '@core/components/sidebar/components/flowpilot/window/content/components/chats/flowpilot-chats-container.component';

@Component({
  selector: 'app-flowpilot-sidebar-layout',
  imports: [CdkDrag, FaIconComponent, FlowpilotChatsContainerComponent],
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
  template: `
    <app-flowpilot-chats-container>
      <div class="flex flex-col gap-y-2.5">
        <div class="-mt-px flex items-start justify-between gap-x-2 px-1">
          <span class="dark:text-dune-100 -ml-0.5 pl-1.5 font-medium">
            Chats
          </span>

          <fa-icon
            class="fa-xs dark:hover:bg-dune-800 hover:bg-dune-200/30 size-5 scale-y-[-1] cursor-pointer rounded"
            [icon]="['fas', 'align-left']"
            (click)="closeSidebar()" />
        </div>

        <div class="flex flex-col gap-y-1">
          <ng-content select="[actions]" />
        </div>
      </div>

      <ng-content select="[chats]" />

      <div
        cdkDrag
        class="absolute top-0 right-0 h-full w-1.5 cursor-e-resize"
        (cdkDragStarted)="resizeStart()"
        (cdkDragMoved)="resizeSidebar($event)"
        (cdkDragEnded)="resizeEnd($event)"></div>
    </app-flowpilot-chats-container>
  `,
})
export class FlowpilotSidebarLayoutComponent {
  /** INJECTIONS **/
  /****/
  private readonly sidebarService = inject(FlowpilotSidebarService);

  /** PROPERTIES **/
  /****/
  private readonly sidebarState = this.sidebarService.stateSignal;

  /****/
  private readonly MIN_WIDTH = 200;

  /****/
  private readonly MAX_WIDTH = 400;

  /****/
  private readonly customWidth = signal<number>(this.MIN_WIDTH);

  /****/
  private readonly startWidth = signal<number>(this.MIN_WIDTH);

  /****/
  protected readonly sidebarWidth = computed(() => {
    const state = this.sidebarState();
    return state === 'open' || state === 'opening' ? this.customWidth() : 0;
  });

  public get sidebarWidthSignal() {
    return this.sidebarWidth;
  }

  /****/
  private readonly _resizing = signal(false);

  public get resizingSignal() {
    return this._resizing.asReadonly();
  }

  constructor(library: FaIconLibrary) {
    library.addIcons(faAlignLeft);
  }

  protected resizeStart() {
    this._resizing.set(true);
    this.startWidth.set(this.sidebarWidth());
  }

  protected resizeSidebar(event: CdkDragMove | CdkDragEnd) {
    const dX = event.distance.x;
    this.customWidth.set(
      Math.min(this.MAX_WIDTH, Math.max(this.MIN_WIDTH, this.startWidth() + dX))
    );
    event.source.reset();
  }

  protected resizeEnd(event: CdkDragEnd) {
    this.resizeSidebar(event);
    this._resizing.set(false);
  }

  protected closeSidebar() {
    this.sidebarService.closeSidebar();
  }
}
